import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { useLocation } from 'react-router-dom';

export interface PageHeaderState {
	title?: string;
	description?: string;
	showBackButton?: boolean;
	onBackClick?: () => void;
	headerAction?: {
		onClick: () => void;
		label: string;
	};
}

interface PageHeaderContextValue extends PageHeaderState {
	setPageHeader: (state: PageHeaderState) => void;
	resetPageHeader: () => void;
}

const PageHeaderContext = React.createContext<
	PageHeaderContextValue | undefined
>(undefined);

const defaultContextValue: PageHeaderContextValue = {
	title: undefined,
	description: undefined,
	showBackButton: undefined,
	onBackClick: undefined,
	headerAction: undefined,
	setPageHeader: () => undefined,
	resetPageHeader: () => undefined
};

export const PageHeaderProvider: React.FC<React.PropsWithChildren> = ({
	children
}) => {
	const location = useLocation();
	const [state, setState] = useState<PageHeaderState>({});
	const [version, setVersion] = useState(0);

	// Store page headers per route path to handle route caching
	const headerCacheRef = useRef<Map<string, PageHeaderState>>(new Map());
	const currentPathRef = useRef<string>(location.pathname);
	const lastStateRef = useRef<PageHeaderState>(state);

	// Keep lastStateRef in sync with state
	useEffect(() => {
		lastStateRef.current = state;
	}, [state]);

	// When location changes, restore the cached header for that route
	useEffect(() => {
		const newPath = location.pathname;
		const previousPath = currentPathRef.current;

		if (newPath !== previousPath) {
			// Save current header state to cache before switching
			const lastState = lastStateRef.current;

			if (
				lastState.title ||
				lastState.description ||
				lastState.showBackButton
			) {
				headerCacheRef.current.set(previousPath, { ...lastState });
			}

			// Restore cached header for new route, or clear if none exists
			const cachedHeader = headerCacheRef.current.get(newPath);

			if (cachedHeader) {
				setState(cachedHeader);
				setVersion(v => v + 1);
			} else {
				// Clear header if no cache exists for this route
				setState({});
				setVersion(v => v + 1);
			}

			currentPathRef.current = newPath;
		}
	}, [location.pathname]);

	const setPageHeader = useCallback((value: PageHeaderState) => {
		const newState = {
			title: value.title,
			description: value.description,
			showBackButton: value.showBackButton,
			onBackClick: value.onBackClick,
			headerAction: value.headerAction
		};

		setState(newState);
		setVersion(v => v + 1);

		// Also update cache for current route (use ref to avoid dependency)
		headerCacheRef.current.set(currentPathRef.current, newState);
	}, []); // No dependencies - stable function reference

	const resetPageHeader = useCallback(() => {
		setState({});
		setVersion(v => v + 1);

		// Also clear cache for current route (use ref to avoid dependency)
		headerCacheRef.current.delete(currentPathRef.current);
	}, []); // No dependencies - stable function reference

	const contextValue = useMemo<PageHeaderContextValue>(
		() => ({
			title: state.title,
			description: state.description,
			showBackButton: state.showBackButton,
			onBackClick: state.onBackClick,
			headerAction: state.headerAction,
			setPageHeader,
			resetPageHeader
		}),
		[state, setPageHeader, resetPageHeader, version]
	);

	return (
		<PageHeaderContext.Provider value={contextValue}>
			{children}
		</PageHeaderContext.Provider>
	);
};

export const usePageHeader = (): PageHeaderContextValue => {
	const context = React.useContext(PageHeaderContext);

	return context ?? defaultContextValue;
};
